/* eslint-disable max-len */
import {
    useCallback,
    useState,
    useMemo,
} from 'react'

import Box from '@mui/material/Box'
import {
    prepareMasterConfig,
} from 'app/PoC/DataGridDemoPages/components/SkyNetMasterDataGrid/SkyNetMasterDataGrid.utils'
import {
    SkyNetDomainConfigParams, SkyNetMasterTabConfig,
} from 'app/PoC/DataGridDemoPages/SkyNetDomainV2/SkyNetDomain.types'
import useDetailedViewOutlet
    from 'app/PoC/DataGridDemoPages/components/SkyNetMasterDataGrid/hooks/useDetailedViewOutlet'
import {
    CommonRoutes,
} from 'app/enums/common.enums'
import {
    TableControlTypes,
} from 'app/shared-components/TableControls'
import {
    SkyNetTableTab,
} from 'app/shared-components/SkyNetTable/types/skyNetTable.types'

import SkyNetDataGrid, {
    ColumnConfigMappingType,
} from '../SkyNetDataGrid'
import useSkyNetDataGridDataSource from '../SkyNetDataGrid/hooks/useSkyNetDataGridDataSource'
import SkyNetDataGridTabs from '../SkyNetDataGridTabs'
import useRowSelectionModel from './hooks/useRowSelectionModel'
import SkyNetResizablePanel from './SkyNetResizablePanel'
import useStyles from './SkyNetMasterDataGrid.styles'
import useAllowedActionKeys from './hooks/useAllowedActionKeys'

type SkyNetMasterDataGridProps = {
    filtersUnrelatedToColumns?: ColumnConfigMappingType[],
    config: SkyNetDomainConfigParams<any>,
}

const toolbarControls: TableControlTypes[] = [TableControlTypes.ShowArhived]

const DEFAULT_MAIN_TABLE_IN_DETAILS_VIEW_WIDTH_PERCENT = 20

const SkyNetMasterDataGrid = ({
    filtersUnrelatedToColumns,
    config,
}: Readonly<SkyNetMasterDataGridProps>) => {
    const {
        classes,
    } = useStyles()
    const {
        id,
        isDetailsView,
        locationPathname,
        navigate,
        tablePath,
        Outlet,
        activeTabName,
    } = useDetailedViewOutlet()

    const uniqField = useMemo(() => {
        return config.uniqField || 'id'
    }, [config])

    const tableConfig = useMemo(() => {
        return prepareMasterConfig(config)
    }, [config])

    const tableTabsConfig = useMemo(() => {
        return tableConfig.map(({
            name, url,
        }) => {
            return {
                name,
                url,
            } as SkyNetTableTab
        })
    }, [tableConfig])

    const activeTab = useMemo(() => {
        return tableTabsConfig.findIndex((tab) => {
            return tab.url === activeTabName
        })
    }, [
        activeTabName,
        tableTabsConfig,
    ])

    const {
        columns,
        request,
        requestKey,
        actionComponents: tabActionComponents,
    } = useMemo(() => {
        return (tableConfig[activeTab] || {}) as SkyNetMasterTabConfig<any>
    }, [
        activeTab,
        tableConfig,
    ])
    const actionComponents = useMemo(() => {
        return tabActionComponents || config.actionComponents
    }, [
        config.actionComponents,
        tabActionComponents,
    ])

    const allowedActionKeys = useAllowedActionKeys({
        actionComponents,
    })

    const customDataSource = useSkyNetDataGridDataSource({
        request,
        key: requestKey,
    })

    const onActiveTabChange = useCallback((newActiveTab: number) => {
        navigate(`${tablePath}/${tableTabsConfig[newActiveTab].url}`)
        customDataSource.invalidate()
        // reset to 1st page on next re-render after invalidation
        setTimeout(() => {
            customDataSource.paginationOptions?.onChange(null, 1)
        })
    }, [
        customDataSource,
        navigate,
        tablePath,
        tableTabsConfig,
    ])

    const {
        rowSelectionModel, onRowSelectionModelChange,
    } = useRowSelectionModel(id)

    const onRowClick = useCallback(({
        row,
    }) => {
        if (String(row[uniqField]) !== String(id)) {
            navigate(`./${CommonRoutes.Edit}/${row[uniqField]}`)
        } else {
            navigate('..')
        }
    }, [
        id,
        navigate,
        uniqField,
    ])

    const [
        tableWidthPercent,
        setTableWidthPercent,
    ] = useState<number>(DEFAULT_MAIN_TABLE_IN_DETAILS_VIEW_WIDTH_PERCENT)

    const dataGridElement = useMemo(() => {
        return (
            <SkyNetDataGrid
                columns={columns as unknown as ColumnConfigMappingType[]} // TODO-DK: fix types
                filtersUnrelatedToColumns={filtersUnrelatedToColumns}
                customDataSource={customDataSource}
                toolbarControls={toolbarControls}
                tabsComponent={(
                    <SkyNetDataGridTabs
                        tabs={tableTabsConfig}
                        isDropDownView={isDetailsView}
                        activeTab={activeTab}
                        onChange={onActiveTabChange}
                    />
                )}
                onRowClick={onRowClick}
                isDetailsView={isDetailsView}
                tableWidthPercent={tableWidthPercent}
                initialTableWidthPercent={DEFAULT_MAIN_TABLE_IN_DETAILS_VIEW_WIDTH_PERCENT}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={onRowSelectionModelChange}
                toolbarActions={allowedActionKeys}
            />
        )
    }, [
        activeTab,
        allowedActionKeys,
        columns,
        customDataSource,
        filtersUnrelatedToColumns,
        isDetailsView,
        onActiveTabChange,
        onRowClick,
        onRowSelectionModelChange,
        rowSelectionModel,
        tableTabsConfig,
        tableWidthPercent,
    ])

    return (
        <Box className={classes.container}>
            {isDetailsView ? (
                <SkyNetResizablePanel
                    initialWidthPercent={tableWidthPercent}
                    onWidthChange={setTableWidthPercent}
                >
                    {dataGridElement}
                </SkyNetResizablePanel>
            ) : (
                <Box className={classes.gridWrapper}>
                    {dataGridElement}
                </Box>
            )}
            <Outlet key={locationPathname} />
        </Box>
    )
}

export default SkyNetMasterDataGrid
